import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import useAgentDetails from '../hooks/useAgentDetails';
import AgentInfoBanner from '../components/agents/AgentInfoBanner';
import ClientsTab from '../components/agents/ClientsTab';
import FlightsTab from '../components/agents/FlightsTab';
import WhatsAppNotificationsTab from '../components/agents/WhatsAppNotificationsTab';
import { Users, Plane, MessageCircle, Loader2, ArrowLeft, Info } from 'lucide-react';

/**
 * Agent Info Page component
 * @returns {JSX.Element} AgentInfoPage component
 */
const AgentInfoPage = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('agentInfo');

  const {
    agentInfo,
    clientInfo,
    flightInfo,
    whatsappNotifications,
    loading,
    error,
    refreshAgentDetails
  } = useAgentDetails(agentId);

  const handleBack = () => {
    navigate('/agents');
  };

  const handleFlightClick = (flight) => {
    // Navigate to Business4Flyo check-in details page
    if (flight.ticketId) {
      navigate(`/business4flyo/check-in/${flight.ticketId}`);
    }
  };

  const tabs = [
    {
      key: 'agentInfo',
      label: 'Agent Info',
      icon: Info,
      count: null
    },
    {
      key: 'clients',
      label: 'Clients',
      icon: Users,
      count: (clientInfo?.individual?.length || 0) + (clientInfo?.corporate?.length || 0)
    },
    {
      key: 'flights',
      label: 'Flights',
      icon: Plane,
      count: flightInfo?.length || 0
    },
    {
      key: 'whatsapp',
      label: 'WhatsApp',
      icon: MessageCircle,
      count: whatsappNotifications?.length || 0
    }
  ];

  if (loading) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center h-screen">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mb-4" />
          <p className="text-gray-600">Loading agent details...</p>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center h-screen">
          <div className="bg-red-100 text-red-700 p-6 rounded-lg mb-4 max-w-md">
            <p className="font-medium">Error loading agent details</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleBack}
              className="px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
            >
              Go Back
            </button>
            <button
              onClick={refreshAgentDetails}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="flex flex-col h-screen overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-4 bg-white border-b">
          <div className="flex items-center gap-4">
            <button
              onClick={handleBack}
              className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm">Back to Agents</span>
            </button>
            <div className="h-6 w-px bg-gray-200" />
            <h1 className="text-xl font-semibold text-gray-900">Agent Details</h1>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="flex-shrink-0 px-6 border-b border-gray-200 bg-white">
          <div className="flex gap-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.key
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                  {tab.count !== null && (
                    <span className={`ml-1 px-2 py-0.5 text-xs rounded-full ${
                      activeTab === tab.key
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {activeTab === 'agentInfo' && (
            <AgentInfoBanner agentInfo={agentInfo} />
          )}
          {activeTab === 'clients' && (
            <ClientsTab clientInfo={clientInfo} />
          )}
          {activeTab === 'flights' && (
            <FlightsTab
              flights={flightInfo}
              onFlightClick={handleFlightClick}
            />
          )}
          {activeTab === 'whatsapp' && (
            <WhatsAppNotificationsTab notifications={whatsappNotifications} />
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default AgentInfoPage;
